import {
  TrustpilotDisplayReview,
  useTrustpilotPagination,
  type ReviewType,
} from "@made-people/grease-trustpilot";
import { useMemo } from "react";

interface UseReviewsLoadMoreResult {
  reviews: TrustpilotDisplayReview[];
  loadMore: () => void;
  canLoadMore: boolean;
  isLoading: boolean;
}

interface UseReviewsLoadMoreParams {
  initialReviews: TrustpilotDisplayReview[];
  type: ReviewType;
  productSku?: string;
  totalReviews?: number;
  availableReviews?: TrustpilotDisplayReview[];
}

/**
 * Miss Mary wrapper for the generic useTrustpilotPagination hook.
 *
 * All Trustpilot configuration (apiKey, businessUnitId, siteLanguage) is provided
 * via the TrustpilotProvider context. See bootstrap.tsx for configuration.
 *
 * @param initialReviews - The initial set of reviews to display (typically from SSR/cache)
 * @param type - Whether to fetch "product" or "store" reviews
 * @param productSku - Required when type is "product", the SKU to fetch reviews for
 * @param totalReviews - Total number of reviews available (used to determine if more can be loaded)
 * @param availableReviews - All reviews currently available in cache/SSR data
 *
 * @returns Object containing:
 *   - reviews: Current list of reviews to display
 *   - loadMore: Function to load next page of reviews
 *   - canLoadMore: Whether more reviews are available to load
 *   - isLoading: Whether a pagination request is in progress
 *
 * For the underlying implementation, see @made-people/grease-trustpilot
 */
export const useReviewsLoadMore = (
  initialReviews: TrustpilotDisplayReview[],
  type: ReviewType,
  productSku?: string,
  totalReviews?: number,
  availableReviews?: TrustpilotDisplayReview[],
): UseReviewsLoadMoreResult => {
  const params = useMemo<UseReviewsLoadMoreParams>(
    () => ({
      type,
      initialReviews,
      totalReviews: totalReviews ?? 0,
      availableReviews: availableReviews ?? [],
      productSku,
    }),
    [type, initialReviews, totalReviews, availableReviews, productSku],
  );

  if (type === "product" && !productSku) {
    console.warn(
      "[useReviewsLoadMore] productSku is required when type is 'product'",
    );
  }

  return useTrustpilotPagination(params);
};
